<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA
    import { __, remToPx, getActualKey } from '@oat-sa-private/ui-core';
    import { selectedInteractions } from '@/store/interactions';
    import getInteractionUserName from '@/core/utils/getInteractionUserName';
    import { isResetExtraTimeAvailable } from '@/core/utils/getAvailableActions';
    import { actionsList, actionsIcons, routes } from '@/core/utils/consts';
    import {
        Dropdown,
        Textarea,
        RadioGroup,
        Button,
        Input,
        ScrollableWrapper,
        Label
    } from '@oat-sa-private/ui-elements';
    import { DateTimePicker, ModalDialog, OverlayBox } from '@oat-sa-private/ui-components';
    import router from '@/core/router/router';
    import { postAcs, deleteDeliveryExecution } from '@/core/api/api';
    import { recoverableErrors } from '@/store/recoverableErrors';
    import { onMount } from 'svelte';
    import dynamicKeys from '@/locale/dynamicKeys.js';
    import { getFlatPickrOptions } from '@/core/utils/flatpickr.js';
    import { configurationStore } from '@/store/tenantConfiguration';

    // ACS to display on form, must be an action from actionList
    export let type;

    // Used to control submit button disable state
    let invalidForm = true;
    let invalidManualForm = true;
    let flatpickrInstance = null;
    let confirmationDialogVisible = false;

    export let requiresConfirmation = false;
    export let categories = [];
    export let subcategories = [];

    const incidentTimeModeKeys = {
        current: 'current',
        manual: 'manual'
    };

    const timeOptions = {
        [incidentTimeModeKeys.current]: __('Current time'),
        [incidentTimeModeKeys.manual]: __('Manual time')
    };

    // Model of actionForm data
    const formData = {
        category: '',
        subcategory: '',
        message: '',
        incidentTimeMode: incidentTimeModeKeys.current,
        incidentTime: ''
    };
    let minDate;
    let maxDate;
    let defaultHour;
    let defaultMinute;

    $: flatPickrOptions = ($configurationStore, getFlatPickrOptions('UTC'));
    $: dateTimePickerOptions = {
        ...flatPickrOptions,
        minuteIncrement: 1,
        minDate,
        maxDate,
        defaultHour,
        defaultMinute,
        /**
         * Set incidentTime value on formData and validate form. When flatpickr is cleared,
         * selectedDates[0] is undefined, so we set formData.incidentTime to its default value
         * @param {Array} selectedDates - Array containing a Date object for selected date
         */
        onChange: function (selectedDates) {
            invalidManualForm = false;
            formData.incidentTime = selectedDates[0] || '';
            checkValidForm();
        },
        /**
         * Sets flatPicker instance into component property so can be controlled from it
         * @param {Array} selectedDates - Array containing a Date object for selected date
         * @param {string} dateStr - Selected date
         * @param {object} instance - flatPicker instance
         *
         */
        onReady: function (selectedDates, dateStr, instance) {
            flatPickrOptions.onReady?.(selectedDates, dateStr, instance);
            flatpickrInstance = instance;
        }
    };

    // disables navigation when there is no internet connection
    $: formDisabled = $recoverableErrors.internetConnection.status;
    // shows subcategories dropdown if there are more than one subcategory to select
    $: showSubcategoriesDropdown =
        typeof subcategories[formData.category] === 'object' &&
        Object.keys(subcategories[formData.category]).length > 1;

    // Calculations to provide correct height to ScrollableWrapper
    let windowHeight = 0;
    const overlayBoxHeaderHeight = remToPx(5.5);
    const paddingsHeight = remToPx(3 + 3 + 0.75);
    const nonScrollingHeight = overlayBoxHeaderHeight + paddingsHeight;
    $: scrollableWrapperCssHeight = `${windowHeight - nonScrollingHeight}px`;

    let wrapperElement;
    onMount(() => {
        // Set the min Date for flatpickr to be equal or higher to the start of the test
        let dates = [];
        checkCurrentTime();
        $selectedInteractions.forEach(interaction => {
            dates.push(interaction.deliveryExecution.startTs);
        });
        const sortedDates = dates.sort();
        const earliestStartDate = sortedDates[0];
        minDate = new Date(earliestStartDate).toISOString();

        wrapperElement.querySelector('.overlay-box')?.focus();
    });

    /**
     * Updates the maxDate and defaultDate of flatpickr
     */
    function updateDate() {
        maxDate = new Date().toISOString();
        defaultHour = new Date().getHours();
        defaultMinute = new Date().getMinutes();
    }

    /**
     * Submits actionForm and redirects to monitoring page on success
     * @param {Event} [e] - Form submit event
     */
    function submitForm(e) {
        if (typeof e !== 'undefined') {
            e.preventDefault();
        }

        const data = buildAcsData();

        if (type === actionsList.reset) {
            deleteDeliveryExecution(data);
        } else {
            postAcs(data);
        }

        router.redirect(routes.home);
    }

    /**
     * Builds an array with the required data for each ACS request
     * @returns {Array} - Array containing each ACS data
     */
    function buildAcsData() {
        let data = [];
        $selectedInteractions.forEach(interaction => {
            const action = Object.keys(actionsList).find(key => actionsList[key] === type);
            const interactionAcs = {
                resource_link_id: interaction.resourceLinkId || '',
                delivery_execution_id: interaction.deliveryExecution.id,
                userName: interaction.user.name,
                sub: interaction.user.id,
                action,
                incident_time:
                    formData.incidentTimeMode === incidentTimeModeKeys.manual
                        ? formData.incidentTime.toISOString()
                        : new Date().toISOString(),
                incident_time_manual: formData.incidentTimeMode === incidentTimeModeKeys.manual,
                reason_code: formData.subcategory,
                reason_msg: formData.message
            };
            if (type === actionsList.extendTime) {
                const currentExtraTime = interaction.timers.extra?.maxTime || 0;
                const currentExtraMinutes = Math.floor(currentExtraTime / 60000);
                interactionAcs.extra_time = currentExtraMinutes + parseInt(formData.extraTime);
            } else if (type === actionsList.resetExtraTime) {
                if (isResetExtraTimeAvailable(interaction)) {
                    interactionAcs.extra_time = 0;
                } else {
                    return; // Skip this interaction
                }
            }
            data.push(interactionAcs);
        });

        return data;
    }
    /**
     * Sets invalidManualForm value. This makes submit btn disabled if Manual time
     * is picked as default until a date is picked
     */
    function checkCurrentTime() {
        if (formData.incidentTimeMode === incidentTimeModeKeys.current) {
            invalidManualForm = false;
        } else {
            invalidManualForm = true;
        }
    }
    /**
     * Sets invalidForm value. If there is any value set as '', form is considered
     * invalid, except if time mode is set to current and empty value is
     * incidentTime (it will be set to current Date on submit event)
     */
    function checkValidForm() {
        if (type === actionsList.extendTime) {
            if (
                isNaN(formData.extraTime) ||
                formData.extraTime < 0 ||
                formData.extraTime === '' ||
                formData.extraTime === null
            ) {
                invalidForm = true;
                return;
            }
        }
        invalidForm =
            typeof Object.entries(formData)
                .filter(
                    field =>
                        formData.incidentTimeMode === incidentTimeModeKeys.manual ||
                        (formData.incidentTimeMode === incidentTimeModeKeys.current && field[0] !== 'incidentTime')
                )
                .find(field => field[1] === '') !== 'undefined';
    }

    /**
     *
     * @param {Event} evt
     * @param {Object} evt.detail
     * @param {string} evt.detail.key - button key to distinguish actions
     */
    function handleDialogAction({ detail: { key } }) {
        switch (key) {
            case 'submit':
                submitForm();
                break;
            case 'cancel':
                hideConfirmationDialog();
                break;
            default:
                break;
        }
    }

    /**
     * Shows termination confirmation dialog
     * @param {Event} e - form submit event to prevent
     */
    function showConfirmationDialog(e) {
        e.preventDefault();
        confirmationDialogVisible = true;
    }

    /**
     * Hides termination confirmation dialog
     */
    function hideConfirmationDialog() {
        confirmationDialogVisible = false;
    }

    /**
     * If selected category has only one subcategory - then sets formData.subcategory to id of this subcategory
     * Else resets formData.subcategory to ''
     * Also checks form validity
     */
    function handleCategoryChange() {
        const categorySubcategories = subcategories[formData.category];
        if (typeof categorySubcategories === 'object' && Object.keys(categorySubcategories).length === 1) {
            formData.subcategory = Object.keys(categorySubcategories)[0];
        } else {
            formData.subcategory = '';
        }
        checkValidForm();
    }

    /**
     * Builds dynamic key to get translations from
     * @param {String} container - Container to translate ('form' or 'dialog')
     * @param {String} field - Target to translate ('header' or 'button')
     * @returns {String} - Translated messag
     */
    function buildTranslationKeys(container, field) {
        const config = {
            length: $selectedInteractions.length,
            userName: getInteractionUserName($selectedInteractions[0].user)
        };
        return dynamicKeys(config)[
            `${container}-${type}-${$selectedInteractions.length > 1 ? 'plural' : 'singular'}-${field}`
        ];
    }

    /**
     * Handle OverlayBox event
     * @param {CustomEvent} e
     */
    function handleOverlayBoxAction(e) {
        if (e.detail.key === 'close') {
            closeOverlayBox();
        }
    }

    function closeOverlayBox() {
        // On closing this route we may want to show / or /details routes
        // It's essentially a 'back', so use that
        router.goBack();
    }

    /**
     * Handle key presses
     * @param {KeyboardEvent} event
     */
    function handleKeydown(event) {
        const keyname = getActualKey(event);
        if (keyname === 'esc') {
            event.stopPropagation();
            closeOverlayBox();
        }
    }
</script>

<style>
    .wrapper {
        height: 100%;
        padding: 3rem 4rem;

        & :global(.overlay-box) {
            height: 100%;
        }
        & :global(.overlay-box-content) {
            padding: 0;
        }
        & :global(.scrollable-wrapper) {
            padding: 2rem;
        }
        & form {
            width: 50%;
            margin: 2rem 25% 6rem;
        }
        @media screen and (--mq-maxwidth-medium) {
            & form {
                width: 80%;
                margin: 2rem 10% 6rem;
            }
        }
        & .form-header {
            font-size: var(--fontsize-heading-l);
            font-weight: bold;
        }
        & .form-body {
            border-top: 0.625rem solid black;

            & > div {
                margin: 5rem 0;
            }
            & .cause-dropdowns-container {
                display: flex;
                justify-content: space-between;
            }
            & .time-inputs-container {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
            }
            & .time-dateTimePicker-container {
                width: 49%;
            }
            & .field-description {
                margin-bottom: 1rem;
                color: var(--color-text-disabled);
                font-size: var(--fontsize-body-s);
            }
            & :global(.cause .cause-dropdowns-container > .select) {
                width: 49%;
            }
            & .extend_time_minutes {
                padding-left: 1rem;
            }
        }
        & .form-footer {
            display: flex;
            justify-content: flex-end;
        }
    }
</style>

<svelte:window bind:innerHeight={windowHeight} on:keydown={handleKeydown} />

<div class="wrapper" bind:this={wrapperElement}>
    <OverlayBox on:action={handleOverlayBoxAction}>
        <ScrollableWrapper height={scrollableWrapperCssHeight}>
            <form on:submit={requiresConfirmation ? showConfirmationDialog : submitForm}>
                <div class="form-header">
                    <p>{buildTranslationKeys('form', 'header')}</p>
                </div>
                <div class="form-body">
                    {#if type === actionsList.extendTime}
                        <div class="extend_time">
                            <Label label={__('Additional time')} required fullwidth>
                                <p class="field-description">
                                    {__(
                                        'Increases the total session time for the selected test takers. This amount will be added to the extra time previously set, if any.'
                                    )}
                                </p>
                                <Input
                                    name="extraTime"
                                    bind:value={formData.extraTime}
                                    disabled={formDisabled}
                                    on:change={checkValidForm}
                                    feedback="inline"
                                    feedbackProps={{ showValidFeedback: false }}
                                    required={true}
                                    type="number"
                                    pattern="^\s*[0-9]*\s*$"
                                    patternMessage={__('Positive integer number or 0')} /><span
                                    class="extend_time_minutes">{__.p('minute', 'minutes', 0)}</span>
                            </Label>
                        </div>
                    {/if}
                    <div class="cause">
                        <Label label={__('Cause')} required fullwidth>
                            <div class="cause-dropdowns-container">
                                <Dropdown
                                    bind:value={formData.category}
                                    options={categories}
                                    placeholder={__('Select an issue')}
                                    required={true}
                                    reset={false}
                                    disabled={formDisabled}
                                    on:change={handleCategoryChange} />
                                {#if showSubcategoriesDropdown}
                                    <Dropdown
                                        bind:value={formData.subcategory}
                                        options={subcategories[formData.category] || []}
                                        placeholder={__('Select a subcategory')}
                                        required={true}
                                        reset={false}
                                        disabled={formDisabled}
                                        on:change={checkValidForm} />
                                {/if}
                            </div>
                        </Label>
                    </div>
                    <div class="commentary">
                        <div>
                            <Label label={`${__('Commentary')} / ${__('Reason')}`} required fullwidth>
                                <Textarea
                                    bind:value={formData.message}
                                    fullwidth={true}
                                    required={true}
                                    disabled={formDisabled}
                                    on:change={checkValidForm} />
                            </Label>
                        </div>
                    </div>
                    {#if type === actionsList.report}
                        <div class="time">
                            <Label label={__('Time')} fullwidth>
                                <div class="time-inputs-container">
                                    <div class="timer-radio-container">
                                        <RadioGroup
                                            bind:value={formData.incidentTimeMode}
                                            options={timeOptions}
                                            layout="grid"
                                            disabled={formDisabled}
                                            on:change={checkValidForm}
                                            on:change={checkCurrentTime} />
                                    </div>
                                    {#if formData.incidentTimeMode === incidentTimeModeKeys.manual}
                                        <div class="time-dateTimePicker-container" use:updateDate>
                                            <DateTimePicker
                                                placeholder=""
                                                width="flexible"
                                                value=""
                                                options={{
                                                    ...dateTimePickerOptions,
                                                    onValueUpdate: function (selectedDates, dateStr, instance) {
                                                        // Only normalize am/pm casing when the visible value uses 12h.
                                                        const dateValue = instance.altInput.value;
                                                        if (!/[ap]m$/i.test(dateValue)) {
                                                            return dateValue;
                                                        }
                                                        const dateWithoutMeridiem = dateValue.substring(
                                                            0,
                                                            dateValue.length - 2
                                                        );
                                                        const meridiem = dateValue
                                                            .substring(dateValue.length - 2)
                                                            .toLowerCase();
                                                        return (instance.altInput.value = `${dateWithoutMeridiem}${meridiem}`);
                                                    }
                                                }} />
                                        </div>
                                    {/if}
                                </div>
                            </Label>
                        </div>
                    {/if}
                </div>
                <div class="form-footer">
                    <Button
                        label={__('Cancel')}
                        ariaLabel={__('Cancel')}
                        skin="secondary"
                        shape="pill"
                        on:click={closeOverlayBox}
                        disabled={formDisabled} />
                    <Button
                        label={buildTranslationKeys('form', 'button')}
                        ariaLabel={buildTranslationKeys('form', 'button')}
                        icon={`${actionsIcons[type]}-16`}
                        skin="primary"
                        shape="pill"
                        type="submit"
                        disabled={invalidForm || formDisabled || invalidManualForm} />
                </div>
            </form>
        </ScrollableWrapper>
    </OverlayBox>
</div>

<ModalDialog
    open={confirmationDialogVisible}
    heading={buildTranslationKeys('dialog', 'header')}
    buttons={[
        { key: 'cancel', label: __('Cancel'), skin: 'secondary' },
        { key: 'submit', label: buildTranslationKeys('dialog', 'button'), skin: 'primary' }
    ]}
    message={buildTranslationKeys('dialog', 'message')}
    on:action={handleDialogAction}
    on:close={hideConfirmationDialog} />
